import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useCallback, useEffect, useState, type FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Text } from '@eo-locale/react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { AuthApi, PeopleApi } from '../api';
import { BackButton, CopyButton } from '../components';

interface EnableFormFields {
  otp: string;
}

interface DisableFormFields {
  otp: string;
}

export const Enable2FAPage: FC = () => {
  const queryClient = useQueryClient();
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpToken, setOtpToken] = useState<string | null>(null);
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  
  const enableForm = useForm<EnableFormFields>({
    defaultValues: {},
  });
  
  const disableForm = useForm<DisableFormFields>({
    defaultValues: {},
  });

  // Проверяем состояние 2FA
  const { data: state, isLoading: isStateLoading, refetch: refetchState } = useQuery({
    queryKey: ['2fa-settings-state'],
    queryFn: AuthApi.getTwoFactorSettingsState,
  });

  // Запрашиваем настройки 2FA при загрузке страницы (если 2FA не включена)
  const { mutate: enableSettings, isLoading: isEnabling } = useMutation({
    mutationFn: AuthApi.enableTwoFactorSettings,
    onSuccess: (data) => {
      setQrImageUrl(AuthApi.getQRCodeImageUrl(data.qrId));
      setSecret(data.secret);
      setOtpToken(data.otpToken);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to initialize 2FA settings');
    },
  });

  // Включаем 2FA после ввода OTP
  const { mutate: confirmEnable, isLoading: isConfirming } = useMutation({
    mutationFn: AuthApi.confirmEnableTwoFactor,
    onSuccess: () => {
      toast.success('2FA enabled successfully!');
      enableForm.reset();
      refetchState();
      // Обновляем профиль, чтобы обновить признак is2faEnabled
      queryClient.invalidateQueries({
        queryKey: [PeopleApi.KEY, 'me'],
      });
    },
    onError: (error: any) => {
      const status = error.response?.status;
      const message = error.response?.data?.message || 'Failed to enable 2FA. Please check your OTP code.';
      
      if (status === 403) {
        toast.error('Invalid OTP code. Please try again.');
      } else if (status === 401) {
        toast.error('Authentication failed. Please try again.');
      } else {
        toast.error(message);
      }
    },
  });

  // Отключаем 2FA
  const { mutate: disable2FA, isLoading: isDisabling } = useMutation({
    mutationFn: AuthApi.disableTwoFactor,
    onSuccess: () => {
      toast.success('2FA disabled successfully!');
      setIsDisableModalOpen(false);
      disableForm.reset();
      refetchState();
      // Обновляем профиль, чтобы обновить признак is2faEnabled
      queryClient.invalidateQueries({
        queryKey: [PeopleApi.KEY, 'me'],
      });
    },
    onError: (error: any) => {
      const status = error.response?.status;
      const message = error.response?.data?.message || 'Failed to disable 2FA. Please check your OTP code.';
      
      if (status === 403) {
        toast.error('Invalid OTP code. Please try again.');
      } else if (status === 401) {
        toast.error('Authentication failed. Please try again.');
      } else {
        toast.error(message);
      }
    },
  });

  // Автоматически запрашиваем настройки, если 2FA не включена
  useEffect(() => {
    if (state && !state.is2faEnabled && !qrImageUrl && !isEnabling) {
      enableSettings();
    }
  }, [state, qrImageUrl, isEnabling, enableSettings]);

  const onEnableSubmit: SubmitHandler<EnableFormFields> = useCallback(
    (data) => {
      if (otpToken) {
        confirmEnable({
          otp: data.otp,
          otpToken: otpToken,
        });
      } else {
        toast.error('2FA settings not initialized. Please refresh the page.');
      }
    },
    [otpToken, confirmEnable],
  );

  const onDisableSubmit: SubmitHandler<DisableFormFields> = useCallback(
    (data) => {
      disable2FA({
        otp: data.otp,
      });
    },
    [disable2FA],
  );

  const enableOtpValue = enableForm.watch('otp') || '';
  const disableOtpValue = disableForm.watch('otp') || '';
  const isEnableButtonDisabled = enableOtpValue.length !== 6 || isConfirming;
  const isDisableConfirmDisabled = disableOtpValue.length !== 6 || isDisabling;

  if (isStateLoading) {
    return (
      <>
        <h1 className="mb-4 text-4xl text-brand-6 font-bold">Settings</h1>
        <div className="p-7 w-3/5 bg-white rounded-3xl">
          <div className="flex items-center justify-center py-8">
            <span>Loading...</span>
          </div>
        </div>
      </>
    );
  }

  // Если 2FA уже включена, показываем кнопку Disable
  if (state?.is2faEnabled) {
    return (
      <>
        <h1 className="mb-4 text-4xl text-brand-6 font-bold">Settings</h1>
        <div className="p-7 w-3/5 bg-white rounded-3xl">
          <div className="mb-8 flex items-center gap-5">
            <BackButton />
            <h2 className="text-lg font-bold">Enable 2FA</h2>
          </div>
          <div className="flex flex-col gap-5">
            <div className="text-center py-4">
              <p className="text-brand-2 mb-6">2FA is currently enabled for your account.</p>
              <button 
                className="button button--block button--primary max-w-xs mx-auto" 
                onClick={() => setIsDisableModalOpen(true)}
              >
                <Text id="settings.2fa.disable" />
              </button>
            </div>
          </div>
        </div>

        {/* Modal for disabling 2FA */}
        <Transition appear show={isDisableModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => setIsDisableModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 mb-4"
                    >
                      <Text id="settings.2fa.disable" />
                    </Dialog.Title>
                    <form onSubmit={disableForm.handleSubmit(onDisableSubmit)} className="grid gap-5">
                      <div>
                        <input
                          {...disableForm.register('otp', {
                            required: 'OTP code is required',
                            pattern: {
                              value: /^\d{6}$/,
                              message: 'OTP code must be 6 digits',
                            },
                          })}
                          className={classNames('input', {
                            'input--invalid': Boolean(disableForm.formState.errors.otp),
                          })}
                          placeholder="Enter 6-digit OTP code"
                          type="text"
                          maxLength={6}
                        />
                        {disableForm.formState.errors.otp && (
                          <span className="text-sm text-red mt-1 block">
                            {disableForm.formState.errors.otp.message}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          className="button button--block button--secondary"
                          onClick={() => {
                            setIsDisableModalOpen(false);
                            disableForm.reset();
                          }}
                        >
                          <Text id="settings.2fa.cancel" />
                        </button>
                        <button
                          type="submit"
                          className="button button--block button--primary"
                          disabled={isDisableConfirmDisabled}
                        >
                          <Text id="settings.2fa.confirm" />
                        </button>
                      </div>
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </>
    );
  }

  return (
    <>
      <h1 className="mb-4 text-4xl text-brand-6 font-bold">Settings</h1>
      <div className="p-7 w-3/5 bg-white rounded-3xl">
        <div className="mb-8 flex items-center gap-5">
          <BackButton />
          <h2 className="text-lg font-bold">Enable 2FA</h2>
        </div>
        
        {isEnabling ? (
          <div className="flex items-center justify-center py-8">
            <span>Initializing 2FA settings...</span>
          </div>
        ) : qrImageUrl && secret ? (
          <div className="grid gap-6">
            {/* QR Code */}
            <div className="flex justify-center">
              <img 
                src={qrImageUrl} 
                alt="QR Code for 2FA" 
                className="border border-brand-3 rounded-lg p-4 bg-white"
              />
            </div>
            
            {/* Secret with Copy button */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-brand-6">Secret Code:</label>
              <div className="flex items-center gap-4">
                <code className="flex-1 px-4 py-3 bg-brand-7 rounded-xl font-mono text-sm text-brand-6">
                  {secret}
                </code>
                <CopyButton value={secret} />
              </div>
            </div>
            
            {/* Instruction */}
            <div className="text-sm text-brand-2">
              <Text id="settings.2fa.instruction" />
            </div>
            
            {/* OTP Input Form */}
            <div className="pt-4 border-t border-brand-3">
              <form className="grid gap-5" onSubmit={enableForm.handleSubmit(onEnableSubmit)}>
                <input
                  {...enableForm.register('otp', {
                    required: 'OTP code is required',
                    pattern: {
                      value: /^\d{6}$/,
                      message: 'OTP code must be 6 digits',
                    },
                  })}
                  className={classNames('input', {
                    'input--invalid': Boolean(enableForm.formState.errors.otp),
                  })}
                  placeholder="Enter 6-digit OTP code"
                  type="text"
                  maxLength={6}
                />
                {enableForm.formState.errors.otp && (
                  <span className="text-sm text-red">{enableForm.formState.errors.otp.message}</span>
                )}
                <button 
                  className="button button--block button--primary" 
                  type="submit"
                  disabled={isEnableButtonDisabled}
                >
                  <Text id="settings.2fa.enable" />
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <span className="text-brand-2">Failed to load 2FA settings. Please try again.</span>
          </div>
        )}
      </div>
    </>
  );
};
